import { v4 } from 'uuid';
import { useState } from 'react';

import type { CSSProperties, ChangeEvent, ReactElement, ComponentProps } from 'react';

import { ReactComponent as Lock } from 'Components/UI_Library/Icons/Lock.svg';
import Loader from '../../LoadingIndicators/Loader';
import inputClasses from '../Inputs.module.css';
import { Label } from '../Label';

type Props = {
    value?: string;
    id?: string;
    errorMessage?: string;
    errorSpacing?: string;
    prefix?: string | ReactElement;
    small?: boolean;
    error?: boolean;
    /** @default 'text' */
    type?: 'password' | 'text' | 'email' | 'url' | 'tel';
    marginTop?: CSSProperties['marginTop'];
    marginBottom?: CSSProperties['marginBottom'];
    containerStyle?: CSSProperties;
    required?: boolean;
    placeholder?: string;
    loading?: boolean;
    pattern?: string;
    defaultValue?: string;
    autoFocus?: boolean;
    name?: string;
    style?: CSSProperties;
    disabled?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
    chip?: ReactElement;
} & Partial<Pick<ComponentProps<typeof Label>, 'label' | 'labelRight'>>;

const TextInput = (props: Props) => {
    const id = props.id || v4();

    const [showPassword, setShowPassword] = useState(false);

    const formatClasses = () => {
        let classes = `${inputClasses.mobilePhoneInput}`;

        if (props.prefix) classes = `${classes} ${inputClasses.withPrefix}`;
        if (props.small) classes = `${classes} ${inputClasses.smallInput}`;
        if (props.error || props.errorMessage) classes = `${classes} ${inputClasses.error}`;

        return classes;
    };

    const formatType = () => {
        if (props.type === 'password' && showPassword) return 'text';
        return props.type || 'text';
    };

    return (
        <div
            style={{
                marginTop: props.marginTop ? props.marginTop : '',
                marginBottom: props.marginBottom ? props.marginBottom : '',
                ...props.containerStyle,
            }}
        >
            {props.label && (
                <Label htmlFor={id} label={props.label} labelRight={props.labelRight} required={props.required} />
            )}
            <div className={inputClasses.inputGroup}>
                <input
                    id={id}
                    className={formatClasses()}
                    type={formatType()}
                    placeholder={props.placeholder}
                    pattern={props.pattern}
                    defaultValue={props.defaultValue}
                    value={props.value}
                    onFocus={props.onFocus}
                    onBlur={props.onBlur}
                    onChange={props.onChange}
                    disabled={props.disabled || props.loading}
                    style={props.style}
                    name={props.name}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- Used when the input is the next action to be taken. Saves a user a click.
                    autoFocus={props.autoFocus}
                />
                {props.chip && <div className={inputClasses.chip}>{props.chip}</div>}
                {!props.chip && !props.loading && props.disabled && (
                    <div className={`${inputClasses.chip} ${inputClasses.lock}`}>
                        <Lock />
                    </div>
                )}
            </div>
            {props.loading && <Loader type="in-form blue" />}
            {props.prefix && (
                <span className={`${inputClasses.prefix} ${!props.value ? inputClasses.empty : ''}`}>
                    {props.prefix}
                </span>
            )}
            {props.type === 'password' && (
                <span
                    className={`${inputClasses.viewPasswordToggle} ${props.errorMessage ? inputClasses.withError : ''}`}
                    onClick={() => setShowPassword(!showPassword)}
                >
                    <i
                        className={inputClasses[`${showPassword ? 'hidePasswordIcon' : 'viewPasswordIcon'}`]}
                        onClick={() => setShowPassword(!showPassword)}
                    />
                </span>
            )}
            {props.errorMessage && <span className={inputClasses.errorMessage}>{props.errorMessage}</span>}
            {!props.errorMessage && props.errorSpacing && (
                <span className={inputClasses.errorSpacing}>{props.errorSpacing}</span>
            )}
        </div>
    );
};

export default TextInput;
